import { read, type Lod } from '@norscom/nap-shared';
import { reportValidationWarnings, type Problem, type Received } from 'validation';

import { getRecordWith } from 'api/shared/getRecord';

/** The problems, each naming the call that received it, so a collapse can say which response it could not read. */
const stamped = (problems: readonly Problem[], source: string): readonly Problem[] => problems.map((problem) => ({ ...problem, source }));

/**
 * The body read with the entity's tree, then reported and stamped as `readResponse` does for a schema.
 *
 * NOTE: `read` stamps no `source` and logs nothing; both are this endpoint's to add.
 */
const readDetails = <T>(lod: Lod<T>, raw: unknown, source: string): Received<T> => {
  const answer = read(lod, raw);
  if (answer.result === 'ok') return answer;
  reportValidationWarnings(source, answer.problems);
  return { ...answer, problems: stamped(answer.problems, source) };
};

/**
 * GET `/details/:type/:id` — one object at a level of detail, for its details pane.
 *
 * The contract is the entity's tree in `@norscom/nap-shared` (the backend's loader
 * guards its result with the same tree), so there is no schema here, and the type in
 * the path is the tree's own entity name. The outcomes are `getRecord`'s: `ok` with
 * `null` is a 404, the server's answer that there is no such object (or none the
 * caller may see, which it does not tell apart); `degraded` and `unreadable` are
 * `read`'s; anything else rejects.
 */
export const getDetails = <T>(lod: Lod<T>, id: number): Promise<Received<T | null>> => {
  const source = `getDetails(${lod.entity})`;
  return getRecordWith(`/details/${lod.entity}/${id}`, (raw) => readDetails(lod, raw, source));
};
